import { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, Search, Phone, X, Clock, User, Heart,
  ChevronDown, CheckCircle2, ArrowUpRight, Activity,
  Stethoscope, ShieldAlert, PhoneCall,
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';

/* ═══ MOCK ═══ */
const ALLARMI = [
  { id: 1, paziente: 'Verdi Luca', eta: 68, setting: 'ADI Alta', tipo: 'SpO2 sotto soglia critica', valore: '86%', soglia: '< 90%', priorita: 'alta', stato: 'da-gestire', timestamp: '4 min fa', caregiver: 'Moglie — Sara Verdi', telefono: '+39 333 1112233', mmg: 'Dott. F. Bianchi', mmgTel: '+39 06 7890123', trend: [96,95,94,92,90,88,86], diagnosi: 'BPCO con insufficienza respiratoria' },
  { id: 2, paziente: 'Esposito Luca', eta: 64, setting: 'ADI Media', tipo: 'Glicemia fuori range', valore: '340 mg/dL', soglia: '> 250 mg/dL', priorita: 'alta', stato: 'da-gestire', timestamp: '12 min fa', caregiver: 'Figlio — Marco Esposito', telefono: '+39 340 5566778', mmg: 'Dott.ssa A. Romano', mmgTel: '+39 06 4567890', trend: [180,210,250,280,310,330,340], diagnosi: 'Diabete T2 — IRC IV stadio' },
  { id: 3, paziente: 'Rossi Anna', eta: 76, setting: 'ADI Alta', tipo: 'PA Sistolica elevata', valore: '182 mmHg', soglia: '> 160 mmHg', priorita: 'alta', stato: 'da-gestire', timestamp: '18 min fa', caregiver: 'Figlia — Laura Rossi', telefono: '+39 339 8877665', mmg: 'Dott. G. Conti', mmgTel: '+39 06 1234567', trend: [135,140,148,155,165,175,182], diagnosi: 'Ipertensione arteriosa — Cardiopatia' },
  { id: 4, paziente: 'Neri Giuseppe', eta: 82, setting: 'ADI Media', tipo: 'Temperatura corporea elevata', valore: '38.9°C', soglia: '> 38.5°C', priorita: 'media', stato: 'da-gestire', timestamp: '25 min fa', caregiver: 'Figlio — Paolo Neri', telefono: '+39 347 2233445', mmg: 'Dott.ssa M. Villa', mmgTel: '+39 06 9876543', trend: [36.8,37.0,37.4,37.8,38.2,38.6,38.9], diagnosi: 'Frattura femore — post-chirurgico' },
  { id: 5, paziente: 'Ferrara Elena', eta: 72, setting: 'ADI Palliativa', tipo: 'FC anomala persistente', valore: '118 bpm', soglia: '> 100 bpm', priorita: 'media', stato: 'da-gestire', timestamp: '32 min fa', caregiver: 'Marito — Carlo Ferrara', telefono: '+39 328 6677889', mmg: 'Dott. L. Marchetti', mmgTel: '+39 06 5432109', trend: [82,88,95,100,105,112,118], diagnosi: 'Neoplasia mammaria metastatica' },
  { id: 6, paziente: 'Colombo Maria', eta: 88, setting: 'ADI Alta', tipo: 'Caduta rilevata da sensore', valore: 'Evento', soglia: 'Accelerometro', priorita: 'media', stato: 'da-gestire', timestamp: '45 min fa', caregiver: 'Figlio — Marco Colombo', telefono: '+39 328 9876543', mmg: 'Dott. F. Bianchi', mmgTel: '+39 06 7890123', trend: null, diagnosi: 'Demenza senile avanzata' },
  { id: 7, paziente: 'Romano Pietro', eta: 71, setting: 'ADI Media', tipo: 'Mancata rilevazione parametri', valore: '> 24h', soglia: 'Nessun dato', priorita: 'media', stato: 'da-gestire', timestamp: '1h fa', caregiver: 'Moglie — Giulia Romano', telefono: '+39 335 1122334', mmg: 'Dott.ssa A. Romano', mmgTel: '+39 06 4567890', trend: null, diagnosi: 'Piede diabetico — medicazioni' },
  { id: 8, paziente: 'Mancini Bruno', eta: 78, setting: 'ADI Bassa', tipo: 'INR fuori range terapeutico', valore: '4.8', soglia: '> 3.5', priorita: 'media', stato: 'da-gestire', timestamp: '1h 20min fa', caregiver: 'Figlia — Silvia Mancini', telefono: '+39 338 4455667', mmg: 'Dott. G. Conti', mmgTel: '+39 06 1234567', trend: [2.2,2.5,2.8,3.2,3.8,4.3,4.8], diagnosi: 'TAO — Fibrillazione atriale' },
];

const PRIO_CONFIG = {
  alta: { border: 'border-l-red-500', badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', dot: 'bg-red-500', chartColor: '#ef4444' },
  media: { border: 'border-l-amber-400', badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', chartColor: '#f59e0b' },
};

const ESCALATION_OPTIONS = ['Case Manager di riferimento', 'Medico UVMD', 'Guardia Medica / 118', 'MMG del paziente'];

function MiniSparkline({ data, color }) {
  if (!data) return <span className="text-[10px] text-slate-400 italic">Nessun trend disponibile</span>;
  const chartData = data.map((v, i) => ({ v, i }));
  return (
    <div className="h-10 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <YAxis hide domain={['dataMin - 2', 'dataMax + 2']} />
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function CentraleAllarmi() {
  const [search, setSearch] = useState('');
  const [filterPrio, setFilterPrio] = useState('tutte');
  const [filterStato, setFilterStato] = useState('da-gestire');
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [escalationType, setEscalationType] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [escalationNote, setEscalationNote] = useState('');

  const countAlta = ALLARMI.filter(a => a.priorita === 'alta').length;
  const countMedia = ALLARMI.filter(a => a.priorita === 'media').length;

  const filtered = useMemo(() => {
    return ALLARMI.filter(a => {
      if (search) {
        const q = search.toLowerCase();
        if (!a.paziente.toLowerCase().includes(q) && !a.tipo.toLowerCase().includes(q)) return false;
      }
      if (filterPrio !== 'tutte' && a.priorita !== filterPrio) return false;
      if (filterStato !== 'tutti' && a.stato !== filterStato) return false;
      return true;
    });
  }, [search, filterPrio, filterStato]);

  const openAlarm = (a) => {
    setSelectedAlarm(a);
    setEscalationType('');
    setAssignTo('');
    setEscalationNote('');
  };

  return (
    <AppLayout role="segreteria">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="centrale-allarmi-page">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Centrale Allarmi Clinici</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Monitoraggio parametri e segnalazioni dal territorio</p>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-3 gap-4" data-testid="allarmi-kpi">
          <div className="bg-red-50 dark:bg-red-950/20 rounded-2xl border border-red-200 dark:border-red-800/40 p-5" data-testid="kpi-critici">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">Allarmi Critici</span>
              <ShieldAlert className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-3xl font-bold text-red-700 dark:text-red-400">{countAlta}</p>
            <p className="text-[10px] text-red-500 dark:text-red-400/70 mt-0.5">Priorità alta — intervento immediato</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-200 dark:border-amber-800/40 p-5" data-testid="kpi-moderati">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">Allarmi Moderati</span>
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{countMedia}</p>
            <p className="text-[10px] text-amber-500 dark:text-amber-400/70 mt-0.5">Priorità media — verifica necessaria</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5" data-testid="kpi-gestiti">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Gestiti Oggi</span>
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="text-3xl font-bold text-slate-800 dark:text-white">12</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Archiviati o inoltrati alle équipe</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 flex flex-wrap items-center gap-3" data-testid="allarmi-toolbar">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca paziente o tipo allarme..." className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20" data-testid="search-allarmi" />
          </div>
          <div className="relative">
            <select value={filterPrio} onChange={e => setFilterPrio(e.target.value)} className="h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500/20 appearance-none" data-testid="filter-priorita">
              <option value="tutte">Tutte le priorità</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select value={filterStato} onChange={e => setFilterStato(e.target.value)} className="h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500/20 appearance-none" data-testid="filter-stato">
              <option value="da-gestire">Da gestire</option>
              <option value="tutti">Tutti</option>
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Lista Allarmi */}
        <div className="flex flex-col gap-3" data-testid="allarmi-list">
          {filtered.length === 0 && (
            <div className="text-center py-16 text-sm text-slate-400">Nessun allarme trovato con i filtri selezionati.</div>
          )}
          {filtered.map(a => {
            const pc = PRIO_CONFIG[a.priorita];
            return (
              <div key={a.id} className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm border-l-4 ${pc.border} p-4`} data-testid={`alarm-card-${a.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <p className="text-sm font-bold text-slate-800 dark:text-white">{a.paziente}</p>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{a.eta} anni</span>
                      <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-0 text-[9px] font-bold">{a.setting}</Badge>
                      <Badge className={`${pc.badge} border-0 text-[9px] font-bold gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
                        {a.priorita}
                      </Badge>
                    </div>
                    <p className={`text-base font-bold mb-1 ${a.priorita === 'alta' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {a.tipo}: {a.valore} <span className="text-xs font-normal text-slate-400">(soglia {a.soglia})</span>
                    </p>
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>Rilevato {a.timestamp}</span>
                      <span className="mx-1">·</span>
                      <span>{a.diagnosi}</span>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <button onClick={() => openAlarm(a)} className={`px-4 py-2 rounded-lg text-xs font-semibold text-white transition-colors ${a.priorita === 'alta' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`} data-testid={`prendi-in-carico-${a.id}`}>
                      Prendi in Carico
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ══ DRAWER: GESTIONE ALLARME ══ */}
      {selectedAlarm && (
        <div className="fixed inset-0 z-50" data-testid="drawer-allarme">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedAlarm(null)} />
          <div className="absolute top-0 right-0 h-full w-full max-w-[500px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">

            {/* Header */}
            <div className={`px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0 ${selectedAlarm.priorita === 'alta' ? 'bg-red-50 dark:bg-red-950/20' : 'bg-amber-50 dark:bg-amber-950/20'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${selectedAlarm.priorita === 'alta' ? 'bg-red-100 dark:bg-red-900/40' : 'bg-amber-100 dark:bg-amber-900/40'}`}>
                    <AlertTriangle className={`w-4.5 h-4.5 ${selectedAlarm.priorita === 'alta' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800 dark:text-white">Gestione Allarme</h2>
                    <p className={`text-xs font-semibold ${selectedAlarm.priorita === 'alta' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{selectedAlarm.tipo}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedAlarm(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/50 dark:hover:bg-slate-800 transition-colors" data-testid="drawer-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">

              {/* Sez 1: Paziente & Contatti */}
              <div>
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Dettaglio Paziente & Contatti Rapidi</h3>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700 flex flex-col gap-2.5">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-bold text-slate-800 dark:text-white">{selectedAlarm.paziente}</span>
                    <span className="text-xs text-slate-500">{selectedAlarm.eta} anni</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-slate-400">Diagnosi:</span> <span className="text-slate-700 dark:text-slate-200">{selectedAlarm.diagnosi}</span></div>
                    <div><span className="text-slate-400">Setting:</span> <span className="text-slate-700 dark:text-slate-200">{selectedAlarm.setting}</span></div>
                    <div><span className="text-slate-400">Caregiver:</span> <span className="text-slate-700 dark:text-slate-200">{selectedAlarm.caregiver}</span></div>
                    <div><span className="text-slate-400">MMG:</span> <span className="text-slate-700 dark:text-slate-200">{selectedAlarm.mmg}</span></div>
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors" data-testid="call-caregiver">
                      <PhoneCall className="w-3.5 h-3.5" /> Paziente / Caregiver
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 text-xs font-semibold hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors" data-testid="call-mmg">
                      <Stethoscope className="w-3.5 h-3.5" /> Medico (MMG)
                    </button>
                  </div>
                </div>
              </div>

              {/* Sez 2: Contesto Clinico */}
              <div>
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Contesto Clinico (Ultime 24h)</h3>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{selectedAlarm.tipo}</span>
                    <span className={`text-sm font-bold ${selectedAlarm.priorita === 'alta' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{selectedAlarm.valore}</span>
                  </div>
                  {selectedAlarm.trend ? (
                    <div className="h-16">
                      <MiniSparkline data={selectedAlarm.trend} color={PRIO_CONFIG[selectedAlarm.priorita].chartColor} />
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic py-2">Nessun dato di trend disponibile per questo parametro.</p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-2">
                    {selectedAlarm.trend ? 'Trend in peggioramento progressivo nelle ultime ore. Non si tratta di un picco isolato.' : `Evento singolo rilevato ${selectedAlarm.timestamp}.`}
                  </p>
                </div>
              </div>

              {/* Sez 3: Azione / Escalation */}
              <div>
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Azione / Escalation</h3>
                <div className="flex flex-col gap-3">
                  <label className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${escalationType === 'risolto' ? 'border-emerald-400 dark:border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} data-testid="radio-risolto">
                    <input type="radio" name="escalation" checked={escalationType === 'risolto'} onChange={() => setEscalationType('risolto')} className="w-4 h-4 text-emerald-600 border-slate-300 focus:ring-emerald-500/30" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">Falso Allarme / Risolto al telefono</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">Nessun intervento clinico necessario</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${escalationType === 'escalation' ? 'border-red-400 dark:border-red-600 bg-red-50/50 dark:bg-red-950/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} data-testid="radio-escalation">
                    <input type="radio" name="escalation" checked={escalationType === 'escalation'} onChange={() => setEscalationType('escalation')} className="w-4 h-4 text-red-600 border-slate-300 focus:ring-red-500/30" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">Richiede Intervento Clinico (Escalation)</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">Smista l'allarme a un professionista sanitario</p>
                    </div>
                  </label>

                  {escalationType === 'escalation' && (
                    <div className="pl-4 border-l-2 border-red-400 flex flex-col gap-3 mt-1" data-testid="escalation-fields">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Assegna a:</label>
                        <div className="relative">
                          <select value={assignTo} onChange={e => setAssignTo(e.target.value)} className="w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500/30 appearance-none" data-testid="select-assign">
                            <option value="">Seleziona destinatario...</option>
                            {ESCALATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Note per operatore</label>
                        <textarea value={escalationNote} onChange={e => setEscalationNote(e.target.value)} rows={3} placeholder="Descrivi il contesto e le azioni già intraprese..." className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/30 resize-none" data-testid="escalation-note" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 shrink-0">
              <button
                disabled={!escalationType || (escalationType === 'escalation' && !assignTo)}
                className={`w-full py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2
                  ${escalationType && (escalationType !== 'escalation' || assignTo)
                    ? (escalationType === 'risolto' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20' : 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20')
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed'}`}
                data-testid="btn-conferma-allarme"
              >
                {escalationType === 'risolto' ? <><CheckCircle2 className="w-4 h-4" /> Conferma e Archivia</> : escalationType === 'escalation' ? <><ArrowUpRight className="w-4 h-4" /> Inoltra Alert</> : 'Seleziona un\'azione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
